<template>
    <div class="location-tree">
        <!-- Location Card -->
        <div
            class="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow mb-3"
            :style="{ marginLeft: `${level * 2}rem` }"
        >
            <div class="p-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-4 flex-1">
                        <!-- Type Icon -->
                        <div :class="[
                            'w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0',
                            getTypeColorClass(location.type)
                        ]">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path v-if="location.type === 'building'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                <path v-else-if="location.type === 'floor'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                <path v-else-if="location.type === 'room'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                <path v-else-if="location.type === 'site'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                <path v-else-if="location.type === 'warehouse'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                        </div>

                        <!-- Location Info -->
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center space-x-2">
                                <Link
                                    :href="route('settings.locations.show', location.id)"
                                    class="text-lg font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400"
                                >
                                    {{ location.name }}
                                </Link>
                                <span
                                    v-if="location.code"
                                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                >
                                    {{ location.code }}
                                </span>
                            </div>
                            <div class="mt-1 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                <span class="flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                    {{ getTypeLabel(location.type) }}
                                </span>
                                <span v-if="location.city" class="flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {{ location.city }}
                                </span>
                                <span class="flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    {{ location.assets_count || 0 }} assets
                                </span>
                            </div>
                        </div>

                        <!-- Status & Actions -->
                        <div class="flex items-center space-x-3">
                            <span
                                :class="[
                                    'px-2.5 py-1 text-xs font-medium rounded-full',
                                    location.is_active
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                                ]"
                            >
                                {{ location.is_active ? 'Active' : 'Inactive' }}
                            </span>

                            <!-- Expand/Collapse Button -->
                            <button
                                v-if="location.children && location.children.length > 0"
                                @click="toggleExpand"
                                class="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <svg
                                    class="w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform"
                                    :class="{ 'rotate-180': isExpanded }"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <!-- Child Count Badge -->
                            <span
                                v-if="location.children && location.children.length > 0"
                                class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                            >
                                {{ location.children.length }} {{ location.children.length === 1 ? 'child' : 'children' }}
                            </span>

                            <Link
                                :href="route('settings.locations.show', location.id)"
                                class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>

                <!-- Description -->
                <div v-if="location.description" class="mt-3 text-sm text-gray-600 dark:text-gray-400 pl-16">
                    {{ location.description }}
                </div>
            </div>
        </div>

        <!-- Child Locations -->
        <div v-if="isExpanded && location.children && location.children.length > 0" class="space-y-3">
            <LocationTree
                v-for="child in location.children"
                :key="child.id"
                :location="child"
                :level="level + 1"
            />
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import route from '@/Utils/route';

const props = defineProps({
    location: {
        type: Object,
        required: true,
    },
    level: {
        type: Number,
        default: 0,
    },
});

const isExpanded = ref(true);

const toggleExpand = () => {
    isExpanded.value = !isExpanded.value;
};

const getTypeLabel = (type) => {
    const labels = {
        building: 'Building',
        floor: 'Floor',
        room: 'Room',
        site: 'Site',
        warehouse: 'Warehouse',
        other: 'Other',
    };
    return labels[type] || type;
};

const getTypeColorClass = (type) => {
    const colors = {
        building: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
        floor: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
        room: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
        site: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
        warehouse: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
        other: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    };
    return colors[type] || colors.other;
};
</script>
